import clsx from 'clsx';
import React from 'react';

import userImg from '../../../../assets/user.png';
import greenTick from '../../../../assets/green-tick.svg';
import linkedin from '../../../../assets/linkedin.svg';
import CalendarIcon from '../../../../assets/Svg/CalendarIcon';
import { alertIcon, ctaPurpleRight } from '../../../../assets/hostedassets';
import { isEmpty, isSafeUrl, toTitleCase } from '../../../../utils/utils';
import { DATE_FORMATS, formatAgeLabel, formatDateByFormat, formatTenureYears } from '../../../../utils/dateUtils';
import { KEYBOARD_EVENTS, TabModule } from '../../../../utils/constants/commonConstants';
import { BureauType } from '../CreditBureau/components/Report/CreditBureauConstants';
import { RISK_COLORS } from '../../../RiskIndicators/RiskIndicatorConstants';
import PotentialRiskRedirectIcon from '../../../RiskIndicators/PotentialRiskRedirectIcon';
import { PillCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/PillCellRenderer/PillCellRenderer';
import { CellRendererProps } from '../../../../common/_custom/DataTable/DataTable.types';
// MANAGEMENT_PROFILE_COMPONENT_FIELDS: dispatched on by the COMPONENT renderer registry below.
// The polus seed sets no `componentName` on any of these columns (D6), so dispatch is on
// `column.field` — a `componentName` switch would silently render blank (pattern:
// GstinTable.tsx:263-278).
import { ColumnLockKey, MANAGEMENT_PROFILE_COMPONENT_FIELDS } from './ManagementProfileTabConstants';
import { DirectorTableRow } from './useManagementProfileTabLogic';
import { phoneValueGetter } from './managementProfileValueGetters';
import styles from './ManagementProfileTab.module.scss';

function NameCell({ row }: { row: DirectorTableRow }) {
    return (
        <div className={styles.nameCell}>
            <div className={styles.avatar}>
                <img
                    src={row.imageUrl || userImg}
                    alt={row.name}
                    onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = userImg;
                    }}
                />
            </div>
            <div className={styles.nameInfo}>
                <div className={styles.NameRow}>
                    <span className={styles.directorName}>{toTitleCase(row.name)}</span>
                    {row.linkedinUrl && isSafeUrl(row.linkedinUrl) && (
                        <a
                            href={row.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.LinkedInMark}
                            aria-label={`${row.name} on LinkedIn`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img src={linkedin} alt="" />
                        </a>
                    )}
                </div>
                {formatAgeLabel(row.age) && (
                    <span className={styles.AgeRow}>
                        <CalendarIcon className={styles.AgeIcon} />
                        {formatAgeLabel(row.age)}
                    </span>
                )}
            </div>
        </div>
    );
}

function TenureCell({ row }: { row: DirectorTableRow }) {
    if (!row.tenureBeginDate) return <span className={styles.cellDash}>-</span>;
    return (
        <div className={styles.tenureInfo}>
            <span className={styles.tenureYears}>{formatTenureYears(row.tenureBeginDate, row.tenureEndDate)}</span>
            <span className={styles.tenureDates}>
                ({formatDateByFormat(row.tenureBeginDate, DATE_FORMATS.MONTH_SHORT_YEAR)} -{' '}
                {row.tenureEndDate ? formatDateByFormat(row.tenureEndDate, DATE_FORMATS.MONTH_SHORT_YEAR) : row.isActive ? 'Present' : ''})
            </span>
        </div>
    );
}

function ContactCell({ row }: { row: DirectorTableRow }) {
    if (row.loading) return <span className={styles.cellDash}>-</span>;
    // Same masking rule as the standalone Phone column: a masked vendor value is not the
    // director's number, so this column must not present it as one.
    const phone = phoneValueGetter(row);
    if (!row.email && !phone) return <span className={styles.cellDash}>-</span>;
    return (
        <div className={styles.contactInfo}>
            {row.email && <span className={styles.contactEmail}>{row.email}</span>}
            {phone && <span className={styles.contactPhone}>{phone}</span>}
        </div>
    );
}

// Enter/Space activation for the click-through cells below — they're plain `div`s (not
// `<button>`), so keyboard/screen-reader users need an explicit role + key handler.
function handleActivationKeyDown(e: React.KeyboardEvent<HTMLDivElement>, onNavigate: () => void) {
    if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
        e.preventDefault();
        e.stopPropagation();
        onNavigate();
    }
}

interface AlertCountCellProps {
    row: DirectorTableRow;
    locked: boolean;
    count?: number | null;
    singularLabel: string;
    pluralLabel: string;
    emptyLabel: string;
    onNavigate: () => void;
}

// Shared by riskIndicatorCount and amlPepRiskCount — both share the "red count / grey
// no-alerts / dash" three-state shape, differing only in copy and the click-through target.
function AlertCountCell({ row, locked, count, singularLabel, pluralLabel, emptyLabel, onNavigate }: AlertCountCellProps) {
    const clickable = !locked && !row.loading && !!count;
    return (
        <div
            className={clsx({ [styles.alertClickable]: clickable })}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={
                clickable
                    ? (e) => {
                          e.stopPropagation();
                          onNavigate();
                      }
                    : undefined
            }
            onKeyDown={clickable ? (e) => handleActivationKeyDown(e, onNavigate) : undefined}
        >
            {locked || row.loading || count == null ? (
                <span className={styles.cellDash}>-</span>
            ) : count > 0 ? (
                <span className={styles.alertRed}>
                    <img src={alertIcon} alt="" height="14px" />
                    {`${count} ${count === 1 ? singularLabel : pluralLabel}`}
                </span>
            ) : (
                <span className={styles.alertGrey}>
                    <img src={greenTick} alt="" height="14px" />
                    {emptyLabel}
                </span>
            )}
        </div>
    );
}



/*
    RECUR-27829. The Regulatory Checks badge decision, as a pure function.

    Extracted rather than inlined in the cell so it can be asserted directly, and so the badge's
    rule is demonstrably the SAME one the Risk Indicators cell applies — show the confirmed count
    when there is one, otherwise the probable count. RCPI-10463 was opened because these two
    surfaces disagreed; keeping the rule in one testable place is what stops that recurring.
*/
export type RegulatoryBadge = {
    count?: number | null;
    /** Noun following the count, e.g. "2 Confirmed". */
    label: string;
    /* Always set — both branches of resolveRegulatoryBadge choose one, so consumers need no fallback. */
    color: string;
    /* The cell tint for this state, reused as the hover wash so it cannot contradict the text. */
    backgroundColor: string;
    /** true when UW has not shipped the roll-up and we are reading the legacy bucket count. */
    isFallback: boolean;
};

export function resolveRegulatoryBadge(row: {
    amlCount?: {
        confirmed?: number | null;
        probable?: number | null;
        flaggedCategories?: number | null;
    };
}): RegulatoryBadge {
    const { confirmed, probable, flaggedCategories: legacy } = row.amlCount ?? {};

    // Both null => UW has not shipped. Fall back to the legacy count rather than rendering
    // "No Indicator", which would assert a clean result nobody computed.
    if (confirmed == null && probable == null) {
        return {
            count: legacy ?? null,
            label: legacy === 1 ? 'Indicator' : 'Indicators',
            color: RISK_COLORS.CONFIRMED.color,
            backgroundColor: RISK_COLORS.CONFIRMED.backgroundColor,
            isFallback: true,
        };
    }

    // Confirmed-first, the same precedence the Risk Indicators cell applies. The counts are hit
    // unions, so this is the number the table shows for that entity, not a count of its cells.
    const isConfirmed = (confirmed ?? 0) > 0;
    const palette = isConfirmed ? RISK_COLORS.CONFIRMED : RISK_COLORS.PROBABLE;
    return {
        count: isConfirmed ? confirmed : probable,
        label: isConfirmed ? 'Confirmed' : 'Probable',
        color: palette.color,
        backgroundColor: palette.backgroundColor,
        isFallback: false,
    };
}

/*
    RECUR-27829. The Regulatory Checks badge used to be an AlertCountCell counting non-zero
    checksCount buckets — a proxy for "rows the vendor graded Red". The Risk Indicators table no
    longer reads that grade, so the badge and the detail it links to would disagree by
    construction. That is the invariant RCPI-10463 was opened to fix, so it gets its own cell
    applying the SAME rule as the table: show the confirmed count when there is one, otherwise
    the probable count, in the table's own colours.

    Deliberately NOT folded into AlertCountCell: that component is shared with the Risk Alerts
    column, which has a genuine two-state red/grey shape and must not grow an amber branch it
    never uses.
*/
function RegulatoryChecksCell({
    row,
    locked,
    onNavigate,
}: {
    row: DirectorTableRow;
    locked: boolean;
    onNavigate: () => void;
}) {
    const { count, label, color, backgroundColor } = resolveRegulatoryBadge(row);
    const clickable = !locked && !row.loading && !!count;
    return (
        <div
            className={clsx({ [styles.alertClickable]: clickable })}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={
                clickable
                    ? (e) => {
                          e.stopPropagation();
                          onNavigate();
                      }
                    : undefined
            }
            onKeyDown={clickable ? (e) => handleActivationKeyDown(e, onNavigate) : undefined}
        >
            {locked || row.loading || count == null ? (
                <span className={styles.cellDash}>-</span>
            ) : count > 0 ? (
                <span
                    className={styles.alertBadge}
                    /*
                        The hover wash is driven from here rather than the stylesheet: .alertClickable
                        is shared with the Risk Alerts column, whose badge is always red, so a
                        hardcoded pink hover was tinting an amber Probable badge pink.
                    */
                    style={{ color, ['--badge-hover-bg' as string]: backgroundColor } as React.CSSProperties}
                >
                    {/*
                        The same inline icon the Risk Indicators cell uses, tinted with the badge
                        colour. The hosted alertIcon is a fixed red asset, so a Probable badge
                        rendered amber text beside a red arrow — the two surfaces disagreeing in
                        colour while agreeing on the number.
                    */}
                    <PotentialRiskRedirectIcon className={styles.regulatoryBadgeIcon} color={color} />
                    {`${count} ${label}`}
                </span>
            ) : (
                <span className={styles.alertGrey}>
                    <img src={greenTick} alt="" height="14px" />
                    No Indicator
                </span>
            )}
        </div>
    );
}

function LegalCasesCell({ row, locked, onNavigate }: { row: DirectorTableRow; locked: boolean; onNavigate: () => void }) {
    const count = row.legalCasesCount;
    const clickable = !locked && !row.loading && !!count;
    return (
        <div
            className={clsx({ [styles.alertClickable]: clickable })}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={
                clickable
                    ? (e) => {
                          e.stopPropagation();
                          onNavigate();
                      }
                    : undefined
            }
            onKeyDown={clickable ? (e) => handleActivationKeyDown(e, onNavigate) : undefined}
        >
            {locked || row.loading || count == null ? (
                <span className={styles.cellDash}>-</span>
            ) : count === 0 ? (
                <span className={styles.alertGrey}>
                    <img src={greenTick} alt="" className={styles.CountIcon} />
                    No match found
                </span>
            ) : (
                <span className={styles.alertRed}>
                    <img src={alertIcon} alt="" height="14px" />
                    {`${count} ${count === 1 ? 'Case' : 'Cases'}`}
                </span>
            )}
        </div>
    );
}

function CreditScoreCell({ row, onNavigate }: { row: DirectorTableRow; onNavigate: () => void }) {
    const isClickable = !row.loading && typeof row.creditScore === 'number' && !Number.isNaN(row.creditScore);
    return (
        <div
            className={clsx({ [styles.creditClickable]: isClickable })}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={
                isClickable
                    ? (e) => {
                          e.stopPropagation();
                          onNavigate();
                      }
                    : undefined
            }
            onKeyDown={isClickable ? (e) => handleActivationKeyDown(e, onNavigate) : undefined}
        >
            {isClickable ? (
                <span className={styles.creditScore}>
                    <img src={ctaPurpleRight} alt="" height="14px" />
                    {row.creditScore}
                </span>
            ) : (
                <span className={styles.cellDash}>-</span>
            )}
        </div>
    );
}

interface ManagementProfileComponentRegistryDeps {
    columnLocks: Record<ColumnLockKey, boolean>;
    navigateToTab?: (tabId: string, additionalParams?: Record<string, string>) => void;
}

/**
 * Builds the `componentRenderFnRegistry` handed to `ComponentCellRenderer` for every
 * `COMPONENT`-typed Management Profile column. Lock state and the navigation callback are
 * captured by closure (not via `show`) — column locks gate click-through/cell content, they
 * never hide the column (D6).
 */
export function createManagementProfileComponentRegistry({ columnLocks, navigateToTab }: ManagementProfileComponentRegistryDeps) {
    return function managementProfileComponentRenderFnRegistry({ row, column }: { row: DirectorTableRow; column?: any }) {
        switch (column?.field) {
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.NAME:
                return <NameCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.TENURE:
                return <TenureCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.CONTACT:
                return <ContactCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.RISK_INDICATOR_COUNT:
                return (
                    <AlertCountCell
                        row={row}
                        locked={columnLocks.riskAlerts}
                        count={row.riskIndicatorCount}
                        singularLabel="Alert"
                        pluralLabel="Alerts"
                        emptyLabel="No Alerts"
                        onNavigate={() =>
                            navigateToTab?.(TabModule.RELATED_PARTY, { directorName: row.name ?? '', directorDin: row.din ?? '' })
                        }
                    />
                );
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.AML_PEP_RISK_COUNT:
                return (
                    <RegulatoryChecksCell
                        row={row}
                        locked={columnLocks.regulatoryChecks}
                        onNavigate={() =>
                            navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                directorName: row.name ?? '',
                                directorDin: row.din ?? '',
                                section: 'regulatoryChecks',
                            })
                        }
                    />
                );
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.LEGAL_CASES_COUNT:
                return (
                    <LegalCasesCell
                        row={row}
                        locked={columnLocks.legalCases}
                        onNavigate={() =>
                            navigateToTab?.(TabModule.LEGAL_CHECKS, { directorName: row.name ?? '', directorDin: row.din ?? '' })
                        }
                    />
                );
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.CREDIT_SCORE:
                return (
                    <CreditScoreCell
                        row={row}
                        onNavigate={() =>
                            navigateToTab?.(TabModule.CREDIT_BUREAU, {
                                bureauType: BureauType.ALL,
                                directorName: row.name ?? '',
                                focusDirectorId: row.directorId ?? '',
                            })
                        }
                    />
                );
            default:
                return null;
        }
    };
}

// PILL adapter — PillCellRenderer takes `{label, isActive}`, not `CellRendererProps`
// (pattern: OrgBasedUsersTable.tsx:401-405). `isActive` keeps its bespoke Active/Former
// copy (the system Status column); any other PILL column (admin-created custom field,
// G-5) falls back to a generic boolean pill so it renders something instead of going
// blank — same isEmpty→dash / truthy→pill convention used by every other ad hoc PILL
// renderer in this repo (OrgBasedUsersTable.tsx, MandateSharingDrawer.tsx).
export function ManagementProfileStatusPillRenderer({ value, column }: CellRendererProps) {
    if (column?.field === 'isActive') {
        if (value === null || value === undefined) return <span className={styles.cellDash}>-</span>;
        return <PillCellRenderer label={value ? 'Active' : 'Former'} isActive={!!value} />;
    }
    if (isEmpty(value)) return <span className={styles.cellDash}>-</span>;
    return <PillCellRenderer label={value ? 'Yes' : 'No'} isActive={!!value} />;
}
